/**
 * useExtractAudio — "save this video's audio as an MP3 in the library".
 *
 * Thin wrapper over the extractBunnyAudio callable shared by the project
 * assets tab, the deliverable Final Assets tab, and the client asset detail
 * page. The callable is idempotent (returns `reused: true` when the parent
 * already links an MP3), so a client timeout + retry is safe.
 */
import { ref } from 'vue';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/js/firebase';
import { toastBridge as toast } from '@/services/toastBridge';
import { logger } from '@/services/loggerService';

// Module-level so every surface shares one in-flight set.
const extractingIds = ref([]);

// Full-length videos only (not selects / social cuts), on Bunny Stream with
// at least one transcoded variant to pull audio from.
export function canExtractAudio(asset) {
  return asset?.assetType === 'videos'
    && !!asset.bunnyStream?.videoId
    && (asset.bunnyStream.mp4Variants?.length || 0) > 0;
}

// True when the parent already links an MP3 that is present in `items`
// (the caller's loaded asset list) — the button disables instead of re-calling.
export function hasExtractedAudio(asset, items = []) {
  const id = asset?.extractedAudio?.assetId;
  return !!id && items.some((a) => a?.id === id);
}

export function useExtractAudio() {
  const isExtracting = (assetId) => extractingIds.value.includes(assetId);

  // Resolves to the callable's { newAssetId, path, reused } or null on failure.
  const extractAudio = async (asset) => {
    if (!asset?.id || isExtracting(asset.id)) return null;
    extractingIds.value = [...extractingIds.value, asset.id];
    toast.info('Extracting MP3 — this can take a few minutes for a long video');
    try {
      // ffmpeg on a long episode runs minutes; the SDK default is 70s.
      const fn = httpsCallable(functions, 'extractBunnyAudio', { timeout: 540000 });
      const { data } = await fn({ assetId: asset.id });
      toast.success(data?.reused ? 'MP3 is already in the library' : 'MP3 added to the library');
      return data;
    } catch (err) {
      logger.error('[useExtractAudio] extraction failed', { assetId: asset.id, error: err?.message });
      const reason = (err?.message || '').replace(/^Audio extraction failed: /, '');
      toast.error(reason ? `Could not extract MP3: ${reason}` : 'Could not extract MP3');
      return null;
    } finally {
      extractingIds.value = extractingIds.value.filter((id) => id !== asset.id);
    }
  };

  return { extractAudio, isExtracting, canExtractAudio, hasExtractedAudio };
}
